import { useQuery } from '@tanstack/react-query';

import { bamsShortsApi } from 'api/bamsShorts';

import { BAMS_SHORTS_GC_TIME, BAMS_SHORTS_QUERY_KEY, BAMS_SHORTS_STALE_TIME } from './bamsShortsQueryKeys';

/**
 * SKU-level BAMS detail for the current + prior ISO pack week (brand x facility x SKU).
 *
 * Both weeks are fetched together; a single week failing is tolerated and only a total
 * failure throws. Returns the raw API rows (mapping stays in the component). Gated on the
 * server by the VIEW_BAMS_SHORTS auth role.
 *
 * @param {Object} params
 * @param {{week: number, year: number}} params.cur
 * @param {{week: number, year: number}} params.prv
 * @param {boolean} [params.enabled=true]
 * @returns {import('@tanstack/react-query').UseQueryResult<Array<Object>, Error>}
 */
export const useBamsShortsSkuDetailQuery = ({ cur, enabled = true, prv }) =>
    useQuery({
        queryKey: [BAMS_SHORTS_QUERY_KEY, 'skuDetail', cur?.year, cur?.week, prv?.year, prv?.week],
        enabled: enabled && !!cur && !!prv,
        staleTime: BAMS_SHORTS_STALE_TIME,
        gcTime: BAMS_SHORTS_GC_TIME,
        queryFn: async () => {
            const [s1, s2] = await Promise.all([
                bamsShortsApi.getSkuDetail(cur.week, cur.year),
                bamsShortsApi.getSkuDetail(prv.week, prv.year),
            ]);
            if (s1?.status === false && s2?.status === false) {
                throw new Error(s1?.msg || s2?.msg || 'Could not load SKU detail.');
            }
            return [].concat(s1?.data?.rows || [], s2?.data?.rows || []);
        },
    });
